import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseService } from '../../core/base.service';

/**
 * 兑换记录实体
 */
export interface RedeemRecord {
  /** 主键ID */
  id: number;
  /** 学生ID */
  studentId: number;
  /** 学生姓名 */
  studentName: string;
  /** 奖品ID */
  prizeId: number;
  /** 奖品名称 */
  prizeName: string;
  /** 兑换数量 */
  quantity: number;
  /** 消耗总积分 */
  pointsSpent: number;
  /** 状态: 0=已取消, 1=正常 */
  status: number;
  /** 教师ID */
  teacherId?: number;
  /** 学期ID */
  semesterId?: number;
  /** 兑换时间 */
  redeemTime: string;
  /** 创建时间 */
  addTime?: string;
}

/** 兑换请求参数 */
export interface RedeemParams {
  studentIds: number[];
  prizeId: number;
  quantity: number;
}

/** 单个学生的兑换结果 */
export interface StudentRedeemResult {
  studentId: number;
  studentName: string;
  success: boolean;
  error?: string;
  pointsBefore?: number;
  pointsAfter?: number;
}

/** 批量兑换响应 */
export interface BatchRedeemResult {
  results: StudentRedeemResult[];
  successCount: number;
  failCount: number;
}

/** 取消兑换响应 */
export interface RedeemResult {
  redeemRecord: RedeemRecord;
  pointsBefore: number;
  pointsAfter: number;
}

/** 报价请求参数 */
export interface RedeemQuoteParams {
  prizeId: number;
  studentIds: number[];
  quantity: number;
}

/** 单个学生的逐生报价（Quote 仅展示、不落库；计价权威在服务端） */
export interface RedeemQuoteRow {
  studentId: number;
  studentName: string;
  /** 该生该奖品 status=1 已兑件数 c（派生，不另存） */
  redeemedUnits: number;
  /** 长度 = quantity：c+1 … c+quantity 逐件单价 */
  unitPrices: number[];
  /** 本次总扣 = Σ unitPrices */
  total: number;
  balance: number;
  balanceAfter: number;
  /** balance ≥ total */
  enough: boolean;
}

/** Quote 响应 items：{ results: [...] } */
export interface RedeemQuoteResult {
  results: RedeemQuoteRow[];
}

/** ResultModel 响应体 */
interface ResultBody<T> {
  statusCode: number;
  items?: T;
  msg?: string;
}

@Injectable({ providedIn: 'root' })
export class RedeemRecordService extends BaseService<RedeemRecord> {
  constructor() {
    super('redeemRecord');
  }

  /** 兑换奖品（支持批量学生） */
  redeem(params: RedeemParams): Observable<BatchRedeemResult> {
    return this.http
      .post<ResultBody<BatchRedeemResult>>(
        `${this.apiBaseUrl}/redeemRecord/Redeem`,
        params,
      )
      .pipe(map((res) => this.unwrapResult(res)));
  }

  /** 报价（仅展示、不落库）：按每生各自的已兑件数现算逐件单价/总扣/兑后余额 */
  quote(params: RedeemQuoteParams): Observable<RedeemQuoteRow[]> {
    return this.http
      .post<ResultBody<RedeemQuoteResult>>(
        `${this.apiBaseUrl}/redeemRecord/Quote`,
        params,
      )
      .pipe(map((res) => this.unwrapResult(res).results));
  }

  /** 取消兑换（退款） */
  cancel(id: number): Observable<RedeemResult> {
    return this.http
      .post<ResultBody<RedeemResult>>(
        `${this.apiBaseUrl}/redeemRecord/Cancel`,
        { id },
      )
      .pipe(map((res) => this.unwrapResult(res)));
  }

  private unwrapResult<T>(res: ResultBody<T>): T {
    if (res.statusCode !== 200) {
      throw new Error(res.msg || '操作失败');
    }
    return res.items as T;
  }
}
